import { useEffect, useSyncExternalStore } from "react";

type PageFrame = {
  hasShell: boolean;
  ownSideColumnFromEm: number | null;
  bottomBarPx: number;
  contentBottomInsetPx: number;
};

const FLOATING_GAP_PX = 16;

let shells = 0;
const ownSideColumns: number[] = [];
const bottomBars: number[] = [];
const bottomFloats: number[] = [];
let snapshot: PageFrame = { hasShell: false, ownSideColumnFromEm: null, bottomBarPx: 0, contentBottomInsetPx: 0 };
const listeners = new Set<() => void>();

const publish = () => {
  const bottomBarPx = Math.max(0, ...bottomBars);
  const floatPx = Math.max(0, ...bottomFloats);
  snapshot = {
    hasShell: shells > 0,
    ownSideColumnFromEm: ownSideColumns.length > 0 ? Math.min(...ownSideColumns) : null,
    bottomBarPx,
    contentBottomInsetPx: bottomBarPx + (floatPx > 0 ? FLOATING_GAP_PX + floatPx : 0),
  };
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useMountsShell = () =>
  useEffect(() => {
    shells += 1;
    publish();
    return () => {
      shells -= 1;
      publish();
    };
  }, []);

export const useHasOwnSideColumn = ({ fromEm }: { fromEm: number }) =>
  useEffect(() => {
    ownSideColumns.push(fromEm);
    publish();
    return () => {
      ownSideColumns.splice(ownSideColumns.indexOf(fromEm), 1);
      publish();
    };
  }, [fromEm]);

export const useHasBottomBar = ({ heightPx }: { heightPx: number }) => useRegistersHeight(bottomBars, heightPx);

const useRegistersHeight = (registry: number[], heightPx: number) =>
  useEffect(() => {
    registry.push(heightPx);
    publish();
    return () => {
      registry.splice(registry.indexOf(heightPx), 1);
      publish();
    };
  }, [registry, heightPx]);

export const useFloatsAboveBottomBar = ({ heightPx }: { heightPx: number }) =>
  useRegistersHeight(bottomFloats, heightPx);

export const usePageFrame = (): PageFrame => useSyncExternalStore(subscribe, () => snapshot);
